import { useEffect, useState } from 'react';

import { Button } from '@/components/Button/Button';
import { ChatHtmlRenderer } from '@/features/chat/ChatHtmlRenderer';

import type {
  AIHomeworkVoteState,
  PracticeQuestionParsed,
  PracticeQuestionResponse,
} from './aiHomeworkTypes';
import { VoteButtons } from './VoteButtons';
import styles from './PracticeQuestionDisplay.module.css';

interface PracticeQuestionDisplayProps {
  error: string | null;
  isAnswerRevealed: boolean;
  isLoading: boolean;
  isRegenerating: boolean;
  onDownvote: () => void;
  onRequestAnother: () => void;
  onSelectAnswer: (index: number) => void;
  onUpvote: () => void;
  question: PracticeQuestionParsed | null;
  response: PracticeQuestionResponse | null;
  selectedAnswer: number | null;
  statusMessage: string | null;
  vote: AIHomeworkVoteState;
}

function getOptionLabel(index: number) {
  return String.fromCharCode(65 + index);
}

function getAnswerText(question: PracticeQuestionParsed) {
  const { answerIndex, answerText, options } = question;

  if (options.length > 0 && typeof answerIndex === 'number') {
    const option = options[answerIndex];

    if (option) {
      return `${getOptionLabel(answerIndex)}. ${option}`;
    }

    return `Option ${answerIndex}`;
  }

  return answerText ?? '';
}

export function PracticeQuestionDisplay({
  error,
  isAnswerRevealed,
  isLoading,
  isRegenerating,
  onDownvote,
  onRequestAnother,
  onSelectAnswer,
  onUpvote,
  question,
  response,
  selectedAnswer,
  statusMessage,
  vote,
}: PracticeQuestionDisplayProps) {
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => {
    setShowAnswer(false);
  }, [question?.text, response?.id]);

  if (!isLoading && !isRegenerating && !error && !statusMessage && !question) {
    return null;
  }

  const hasOptions = question ? question.options.length > 0 : false;
  const correctAnswerIndex =
    question && hasOptions && typeof question.answerIndex === 'number'
      ? question.answerIndex
      : null;
  const isCorrect =
    selectedAnswer !== null && correctAnswerIndex !== null
      ? selectedAnswer === correctAnswerIndex
      : false;
  const isCorrectAnswerVisible = showAnswer || isAnswerRevealed;

  return (
    <section className={styles.panel} aria-label="AI practice problem">
      <div className={styles.header}>
        <h4 className={styles.title}>Practice Problem</h4>
        <p className={styles.caption}>
          Try a similar question before returning to the original one.
        </p>
      </div>

      {isLoading ? (
        <p className={styles.status} role="status">
          Generating a practice problem...
        </p>
      ) : null}

      {isRegenerating ? (
        <div className={styles.regenerating} role="status" aria-live="polite">
          <span className={styles.spinner} aria-hidden="true" />
          <p>Sorry about that, let me try again…</p>
        </div>
      ) : null}

      {statusMessage ? (
        <p className={styles.status} role="status">
          {statusMessage}
        </p>
      ) : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      {question && !isRegenerating ? (
        <>
          {question.text ? (
            <div className={styles.content}>
              <ChatHtmlRenderer fullWidth html={question.text} />
            </div>
          ) : null}

          {hasOptions ? (
            <div className={styles.options} role="radiogroup" aria-label="Practice answer options">
              {question.options.map((option, index) => {
                const selected = selectedAnswer === index;
                const correct =
                  isCorrectAnswerVisible && correctAnswerIndex === index;
                const incorrect = isAnswerRevealed && selected && !correct;

                return (
                  <button
                    aria-checked={selected}
                    className={styles.option}
                    data-correct={correct ? 'true' : 'false'}
                    data-incorrect={incorrect ? 'true' : 'false'}
                    data-selected={selected ? 'true' : 'false'}
                    disabled={isAnswerRevealed}
                    key={`${option}-${index}`}
                    onClick={() => onSelectAnswer(index)}
                    role="radio"
                    type="button"
                  >
                    <span className={styles.optionIndex} aria-hidden="true">
                      {getOptionLabel(index)}
                    </span>
                    <span className={styles.optionContent}>
                      <ChatHtmlRenderer fullWidth html={option} />
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className={styles.emptyState}>
              Work this one out on your own, then reveal the answer when
              you're ready to compare.
            </p>
          )}

          {hasOptions && isAnswerRevealed && selectedAnswer !== null ? (
            <p
              className={styles.feedback}
              data-status={isCorrect ? 'correct' : 'incorrect'}
              role="status"
            >
              {isCorrect
                ? 'Correct!'
                : `Incorrect. The correct answer is ${
                    correctAnswerIndex !== null
                      ? getOptionLabel(correctAnswerIndex)
                      : getAnswerText(question)
                  }.`}
            </p>
          ) : null}

          <div className={styles.answerControls}>
            <Button
              aria-expanded={showAnswer}
              onClick={() => setShowAnswer((current) => !current)}
              size="small"
              variant="secondary"
            >
              {showAnswer ? 'Hide Answer' : 'Show Answer'}
            </Button>
            {showAnswer ? (
              <div className={styles.answerReveal} role="status">
                <span className={styles.answerRevealLabel}>
                  The correct answer is:
                </span>
                <div className={styles.answerRevealContent}>
                  <ChatHtmlRenderer fullWidth html={getAnswerText(question)} />
                </div>
              </div>
            ) : null}
          </div>

          <div className={styles.actions}>
            {response ? (
              <VoteButtons
                downvotes={response.downvotes}
                onDownvote={onDownvote}
                onUpvote={onUpvote}
                upvotes={response.upvotes}
                vote={vote}
              />
            ) : null}
            <Button
              disabled={isLoading}
              onClick={onRequestAnother}
              size="small"
              variant="ghost"
            >
              Try another practice problem
            </Button>
          </div>
        </>
      ) : null}
    </section>
  );
}
